import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { FinSightLogo } from '../components/FinSightLogo';
import { ChevronDown } from 'lucide-react';
import { isAiEnabled } from '../utils/aiFeatureFlag';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Menu,
  X,
  UserCircle,
  Settings,
  LogOut,
  Wallet,
  Library,
  FileText,
  Users,
  UserPlus,
  Globe,
  Sparkles,
  MessageSquare,
  Shield,
} from 'lucide-react';

export default function MainLayout({ selectedRole, setSelectedRole, onLogout, onResetOnboarding }) {
  const userEmail = localStorage.getItem('userEmail') || 'User';
  const userName = localStorage.getItem('fullName') || 'User Name';
  // Ensure selectedRole is valid
  const validRole = localStorage.getItem('userRole') || selectedRole;//selectedRole && typeof selectedRole === 'string' ? selectedRole : 'investor';
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  console.log('Mainlayout isResetOnboarding', localStorage.getItem('isResetOnboarding'));
  const isResetOnboarding = localStorage.getItem('isResetOnboarding');
  useEffect(() => {
    if (isResetOnboarding === 'true') {
      console.log('Navigating to /onboarding from MainLayout');
      localStorage.removeItem('isResetOnboarding');
      setIsResetting(true);
      window.location.href = '/onboarding'
      //navigate('/onboarding',{ replace: true });
    }
}, []);

  const userPlan = localStorage.getItem('plan');

  const navItems = [
    { path: '/dashboard', label: 'Portfolio', icon: Wallet },
    { path: '/research', label: 'Research', icon: Library },
    ...(validRole === 'advisor' || validRole === 'analyst' 
      ? [{ path: '/outlook-reviews', label: 'Reviews', icon: FileText }] 
      : []
    ),
    // { path: '/roles', label: 'Roles', icon: Users }, // Hidden for now - revisit later
    { path: '/matching', label: 'Match', icon: UserPlus },
    { path: '/network', label: 'Network', icon: Globe },
    { path: '/ai-assistant', label: 'AI Assistant', icon: Sparkles, disabled: !isAiEnabled() },
    { path: '/team', label: 'Team', icon: MessageSquare },
    ...(userPlan === 'ENTERPRISE' 
      ? [{ path: '/admin', label: 'Admin', icon: Shield }] 
      : []
    ),
    { path: '/profile', label: 'Profile', icon: UserCircle },
  ];

  const handleRoleChange = (role) => {
    // Prevent changing to the same role
    if (role === validRole) {
      return;
    }
    
    localStorage.setItem('userRole', role);
    setSelectedRole(role);
    // Use requestAnimationFrame to defer event dispatch and prevent event propagation issues
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent('roleChanged', { detail: role }));
    });
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-6">
              <FinSightLogo variant="compact" size="xl" />
              <div className="border-l border-border pl-6">
                {/* <h1 className="text-2xl font-semibold">Financial Insights Platform </h1> */}
                <h1 className="text-2xl font-semibold">Wealth Brain AI Hub</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {userName} ({userEmail}) • {validRole.charAt(0).toUpperCase() + validRole.slice(1)}{userPlan === 'ENTERPRISE' ? ' (ENTERPRISE)' : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Switch Profile dropdown hidden for now - revisit later */}
              {/* Settings hidden for now - revisit later */}
              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                className="text-muted-foreground hover:text-destructive"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="border-b bg-background sticky top-[73px] z-10">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 py-2 overflow-x-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const disabled = item.disabled || false;
              return (
                <Button
                  key={item.path}
                  variant={active ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => !disabled && navigate(item.path)}
                  disabled={disabled}
                  className={`gap-2 whitespace-nowrap ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                  title={disabled ? 'AI features are currently disabled' : item.label}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{item.label}</span>
                </Button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-6 py-6">
        <Outlet />
      </main>
    </div>
  );
}
